// pages/QuestionsSignalees.jsx
// Page de gestion des questions de grammaire signalées comme erronées

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Loader2,
  Edit2,
  Check,
  X,
  Trash2,
  CheckCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { getLangueFlag } from '../utils/exerciseUtils';
import './QuestionsSignalees.css';

export default function QuestionsSignalees() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/exercices/quiz-grammaire/signalees');
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors du chargement');
      }

      setQuestions(data.questions || []);
    } catch (err) {
      console.error('Erreur chargement questions signalées:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (question) => {
    setEditingId(question.id);
    setEditForm({
      question: question.question,
      reponses: [...question.reponses],
      indexCorrect: question.indexCorrect,
      explication: question.explication,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (id) => {
    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const data = await response.json();

      if (data.success) {
        setEditingId(null);
        loadQuestions();
      } else {
        alert('Erreur : ' + (data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleCorriger = async (id) => {
    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/${id}/corriger`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();

      if (data.success) {
        loadQuestions();
      } else {
        alert('Erreur : ' + (data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette question ? Cette action est irréversible.')) return;

    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();

      if (data.success) {
        loadQuestions();
      } else {
        alert('Erreur : ' + (data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const updateReponse = (index, value) => {
    const newReponses = [...editForm.reponses];
    newReponses[index] = value;
    setEditForm({ ...editForm, reponses: newReponses });
  };

  const removeReponse = (index) => {
    if (editForm.reponses.length <= 2) {
      alert('Il faut au minimum 2 réponses.');
      return;
    }
    const newReponses = editForm.reponses.filter((_, i) => i !== index);
    let newIndex = editForm.indexCorrect;
    if (index < newIndex) newIndex--;
    else if (index === newIndex) newIndex = 0;
    setEditForm({ ...editForm, reponses: newReponses, indexCorrect: Math.min(newIndex, newReponses.length - 1) });
  };

  const addReponse = () => {
    setEditForm({ ...editForm, reponses: [...editForm.reponses, ''] });
  };

  const moveReponse = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= editForm.reponses.length) return;

    const newReponses = [...editForm.reponses];
    [newReponses[index], newReponses[newIndex]] = [newReponses[newIndex], newReponses[index]];

    // Mettre à jour indexCorrect si la bonne réponse est déplacée
    let newCorrect = editForm.indexCorrect;
    if (editForm.indexCorrect === index) newCorrect = newIndex;
    else if (editForm.indexCorrect === newIndex) newCorrect = index;

    setEditForm({ ...editForm, reponses: newReponses, indexCorrect: newCorrect });
  };

  // ========== RENDU ==========

  if (loading) {
    return (
      <div className="questions-signalees-page">
        <div className="loading-state">
          <Loader2 size={40} className="spin" />
          <p>Chargement des questions signalées...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="questions-signalees-page">
        <div className="error-state">
          <p>{error}</p>
          <button className="btn-retry" onClick={loadQuestions}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="questions-signalees-page">
      <div className="page-header">
        <h1>
          <AlertTriangle size={32} color="#F59E0B" />
          Questions Signalées
        </h1>
        <p className="page-description">
          Gérez les questions de grammaire signalées comme ayant des erreurs.
        </p>
      </div>

      {questions.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} color="#10B981" />
          <p>Aucune question signalée pour le moment</p>
          <p className="empty-hint">Les questions signalées par les utilisateurs apparaîtront ici.</p>
        </div>
      ) : (
        <>
          <p className="questions-count">{questions.length} question{questions.length > 1 ? 's' : ''} signalée{questions.length > 1 ? 's' : ''}</p>
          <div className="questions-list">
            {questions.map((q) => (
              <div key={q.id} className="question-card">
                {/* En-tête */}
                <div className="question-card-header">
                  <div className="question-info">
                    <span className="badge-langue">{getLangueFlag(q.langue)} {q.langue?.toUpperCase()}</span>
                    <span className="badge-theme">{q.pointGrammaire || q.theme}</span>
                    {q.niveauDifficulte && (
                      <span className="badge-niveau">Niv. {q.niveauDifficulte}</span>
                    )}
                  </div>
                  <div className="question-actions">
                    {editingId !== q.id && (
                      <>
                        <button className="btn-action btn-edit" title="Modifier" onClick={() => startEditing(q)}>
                          <Edit2 size={16} />
                        </button>
                        <button className="btn-action btn-validate" title="Marquer comme corrigé" onClick={() => handleCorriger(q.id)}>
                          <Check size={16} />
                        </button>
                        <button className="btn-action btn-delete" title="Supprimer" onClick={() => handleDelete(q.id)}>
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Contenu */}
                {editingId === q.id ? (
                  <div className="question-edit-form">
                    {/* Question */}
                    <label className="form-field">
                      <span>Question</span>
                      <textarea
                        value={editForm.question}
                        onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                        rows={2}
                      />
                    </label>

                    {/* Réponses */}
                    <div className="form-field">
                      <span>Réponses</span>
                      <div className="reponses-edit-list">
                        {editForm.reponses.map((rep, i) => (
                          <div key={i} className="reponse-edit-item">
                            <div className="reponse-move-btns">
                              <button
                                className="btn-move"
                                onClick={() => moveReponse(i, -1)}
                                disabled={i === 0}
                                title="Monter"
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                className="btn-move"
                                onClick={() => moveReponse(i, 1)}
                                disabled={i === editForm.reponses.length - 1}
                                title="Descendre"
                              >
                                <ChevronDown size={14} />
                              </button>
                            </div>
                            <input
                              type="radio"
                              name={`correct-${q.id}`}
                              checked={editForm.indexCorrect === i}
                              onChange={() => setEditForm({ ...editForm, indexCorrect: i })}
                              title="Bonne réponse"
                            />
                            <input
                              type="text"
                              value={rep}
                              onChange={(e) => updateReponse(i, e.target.value)}
                              className={editForm.indexCorrect === i ? 'correct' : ''}
                            />
                            <button
                              className="btn-remove-reponse"
                              onClick={() => removeReponse(i)}
                              title="Retirer cette réponse"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                        <button className="btn-add-reponse" onClick={addReponse}>
                          + Ajouter une réponse
                        </button>
                      </div>
                    </div>

                    {/* Explication */}
                    <label className="form-field">
                      <span>Explication</span>
                      <textarea
                        value={editForm.explication}
                        onChange={(e) => setEditForm({ ...editForm, explication: e.target.value })}
                        rows={3}
                      />
                    </label>

                    {/* Actions du formulaire */}
                    <div className="form-actions">
                      <button className="btn-action btn-save" onClick={() => handleSave(q.id)}>
                        <Check size={16} /> Enregistrer
                      </button>
                      <button className="btn-action btn-cancel" onClick={cancelEditing}>
                        <X size={16} /> Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="question-details">
                    <p className="question-text">{q.question}</p>

                    <div className="reponses-list">
                      {q.reponses.map((rep, i) => (
                        <div
                          key={i}
                          className={`reponse-item ${i === q.indexCorrect ? 'correct' : ''}`}
                        >
                          <span className="reponse-letter">{String.fromCharCode(65 + i)}</span>
                          <span className="reponse-text">{rep}</span>
                          {i === q.indexCorrect && <CheckCircle size={16} className="correct-icon" />}
                        </div>
                      ))}
                    </div>

                    <div className="explication-block">
                      <strong>Explication :</strong> {q.explication}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
